<template>
  <teleport to="body">
    <transition name="commander-fade">
      <div v-if="visible" class="commander-overlay" data-ds="wangshu" @click.self="handleClose">
        <div class="commander-modal" @click.stop @keydown="handleKeyDown">
          <!-- ========== 顶部搜索区 ========== -->
          <div class="search-header">
            <div class="search-icon-wrapper">
              <IconSearch :size="18" />
            </div>
            <input
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="搜索智能体名称、技能或状态..."
              @keydown="handleKeyDown"
              @input="handleSearch"
            />
            <div class="esc-badge">ESC</div>
          </div>

          <!-- ========== 中间列表区 ========== -->
          <div class="list-container" ref="listRef">
            <template v-if="filteredAgents.length > 0">
              <div
                v-for="(agent, index) in filteredAgents"
                :key="agent.key"
                class="agent-item"
                :class="{
                  active: selectedIndex === index,
                  disabled: agent.status === 'offline'
                }"
                @click="agent.status !== 'offline' && handleSelectAgent(agent)"
                @mouseenter="selectedIndex = index"
              >
                <!-- 左侧蓝色指示条 -->
                <div class="indicator" :class="{ visible: selectedIndex === index }" />

                <!-- 头像区域 -->
                <div class="avatar-wrapper">
                  <AgentAvatar
                    :name="agent.name"
                    :short-label="agent.avatarText"
                    :seed="agent.key"
                    :avatar-url="agent.avatarUrl"
                    :image-alt="`${agent.name}头像`"
                    :status="avatarStatus(agent.status)"
                    :size="48"
                  />
                  <div
                    class="status-dot"
                    :style="{ backgroundColor: getStatusConfig(agent.status).color }"
                  />
                </div>

                <!-- 信息区域 -->
                <div class="info-section">
                  <!-- 第一行: 名称 + 职位标签 -->
                  <div class="name-row">
                    <span class="name">{{ agent.name }}</span>
                    <span
                      class="title-badge"
                      :style="{
                        backgroundColor: getColorConfig(agent.color).tagBg,
                        color: getColorConfig(agent.color).tagText
                      }"
                    >
                      {{ agent.title }}
                    </span>
                  </div>

                  <!-- 第二行: 能力 Tags -->
                  <div class="skills-row">
                    <span
                      v-for="(skill, idx) in agent.skills.slice(0, 3)"
                      :key="idx"
                      class="skill-tag"
                    >
                      <span class="skill-icon">
                        <component :is="getSkillIcon(skill.icon)" :size="11" />
                      </span>
                      <span class="skill-text">{{ skill.text }}</span>
                    </span>
                  </div>

                  <!-- 第三行: 状态文本 -->
                  <div class="status-row">
                    <span class="status-icon">{{ getStatusConfig(agent.status).icon }}</span>
                    <span class="status-text">{{ agent.statusText }}</span>
                  </div>
                </div>

                <!-- 右侧: Enter 呼叫按钮 -->
                <div class="action-button" :class="{ visible: selectedIndex === index }">
                  <span class="enter-key">↵</span>
                  <span class="enter-text">呼叫</span>
                </div>
              </div>
            </template>

            <!-- 空状态 -->
            <div v-else class="empty-state">
              <div class="empty-icon">🔍</div>
              <div class="empty-text">未找到匹配的智能体</div>
              <div class="empty-hint">尝试搜索其他关键词</div>
            </div>
          </div>

          <!-- ========== 底部栏 ========== -->
          <div class="footer">
            <div class="footer-left">
              <span class="footer-label">智能体中心</span>
              <span class="footer-count">{{ filteredAgents.length }} 个可用</span>
            </div>
            <div class="footer-right">
              <div class="shortcut-group">
                <span class="shortcut-key">
                  <IconArrowUp :size="12" />
                </span>
                <span class="shortcut-key">
                  <IconArrowDown :size="12" />
                </span>
                <span class="shortcut-label">选择</span>
              </div>
              <div class="shortcut-divider" />
              <div class="shortcut-group">
                <span class="shortcut-key">↵</span>
                <span class="shortcut-label">确认</span>
              </div>
              <div class="shortcut-divider" />
              <div class="shortcut-group">
                <span class="shortcut-key">{{ isMac ? '⌘' : 'Ctrl' }}+K</span>
                <span class="shortcut-label">呼叫</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
import IconArrowDown from '@arco-design/web-vue/es/icon/icon-arrow-down'
import IconArrowRise from '@arco-design/web-vue/es/icon/icon-arrow-rise'
import IconArrowUp from '@arco-design/web-vue/es/icon/icon-arrow-up'
import IconApps from '@arco-design/web-vue/es/icon/icon-apps'
import IconBarChart from '@arco-design/web-vue/es/icon/icon-bar-chart'
import IconBranch from '@arco-design/web-vue/es/icon/icon-branch'
import IconCompass from '@arco-design/web-vue/es/icon/icon-compass'
import IconEdit from '@arco-design/web-vue/es/icon/icon-edit'
import IconExperiment from '@arco-design/web-vue/es/icon/icon-experiment'
import IconFile from '@arco-design/web-vue/es/icon/icon-file'
import IconHeart from '@arco-design/web-vue/es/icon/icon-heart'
import IconLayout from '@arco-design/web-vue/es/icon/icon-layout'
import IconList from '@arco-design/web-vue/es/icon/icon-list'
import IconMessage from '@arco-design/web-vue/es/icon/icon-message'
import IconNotification from '@arco-design/web-vue/es/icon/icon-notification'
import IconPen from '@arco-design/web-vue/es/icon/icon-pen'
import IconSearch from '@arco-design/web-vue/es/icon/icon-search'
import IconVideoCamera from '@arco-design/web-vue/es/icon/icon-video-camera'
import type { Component } from 'vue'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAgentRegistryStore } from '@/stores/agentRegistry'
import { AGENT_CHAT_CONFIGS, type AgentRegistryItem } from '@/constants/agents'
import AgentAvatar from '@/components/shared/AgentAvatar.vue'
import type { AgentAvatarStatus } from '@/utils/agent-avatar'

// ============================================
// 类型定义
// ============================================

interface SkillItem {
  icon: string
  text: string
}

interface AgentEmployee {
  key: string
  name: string
  avatarUrl?: string
  avatarText: string
  title: string
  skills: SkillItem[]
  status: 'online' | 'busy' | 'offline'
  statusText: string
  color: 'blue' | 'cyan' | 'green' | 'purple' | 'pink'
  route?: string
}

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select': [agent: AgentEmployee]
}>()

const router = useRouter()
const agentRegistryStore = useAgentRegistryStore()
const visible = ref(props.modelValue)
const searchQuery = ref('')
const selectedIndex = ref(0)
const searchInputRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLDivElement | null>(null)

// ============================================
// 图标映射
// ============================================

const ICON_MAP: Record<string, Component> = {
  'bar-chart': IconBarChart,
  'arrow-rise': IconArrowRise,
  'branch': IconBranch,
  'layers': IconExperiment,
  'compass': IconCompass,
  'file': IconFile,
  'apps': IconApps,
  'layout': IconLayout,
  'notification': IconNotification,
  'heart': IconHeart,
  'message': IconMessage,
  'list': IconList,
  'pen': IconPen,
  'video-camera': IconVideoCamera,
  'edit': IconEdit
}

function getSkillIcon(iconName: string): Component {
  return ICON_MAP[iconName] || IconApps
}

// ============================================
// 颜色配置
// ============================================

// Claude 素色：所有 Agent 头像统一墨黑，仅 tag 用极淡橙区分
// 移除原本的多彩主题色（蓝/紫/青/绿/粉），与 claude.ai 单色头像一致
const COLOR_CONFIG: Record<string, { gradient: string; tagBg: string; tagText: string }> = {
  blue:   { gradient: '#1F1E1D', tagBg: '#EDEBE3', tagText: '#4A4845' },
  purple: { gradient: '#1F1E1D', tagBg: '#EDEBE3', tagText: '#4A4845' },
  cyan:   { gradient: '#1F1E1D', tagBg: '#EDEBE3', tagText: '#4A4845' },
  green:  { gradient: '#1F1E1D', tagBg: '#E8EFE9', tagText: '#3F6C4D' },
  pink:   { gradient: '#1F1E1D', tagBg: '#F4E8E2', tagText: '#9D5640' }
}

function getColorConfig(color: string) {
  return COLOR_CONFIG[color] || COLOR_CONFIG.blue
}

// ============================================
// 状态配置
// ============================================

const STATUS_CONFIG: Record<string, { color: string; icon: string }> = {
  online: { color: '#3F6C4D', icon: '⚡' },
  busy: { color: '#8B6420', icon: '🔄' },
  offline: { color: '#8A8780', icon: '💤' }
}

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || STATUS_CONFIG.offline
}

// ============================================
// 检测 Mac 系统
// ============================================

const isMac = ref(false)
function detectOS() {
  isMac.value = /Mac|iPhone|iPod|iPad/i.test(navigator.platform) || 
                /Mac|iPhone|iPod|iPad/i.test(navigator.userAgent)
}

// ============================================
// Agent 颜色映射（agent.color hex → 预设主题名）
// ============================================

/** 将 Agent 注册表的 hex 颜色映射到预设主题色 */
// Claude 素色：所有色调都映射到 blue（统一墨黑头像 + bg-subtle 标签）
const AGENT_COLOR_MAP: Record<string, AgentEmployee['color']> = {
  '#9D5640': 'blue',
  '#3b82f6': 'blue',
  '#8B6420': 'pink',
  '#ef4444': 'pink',
  '#14c9c9': 'cyan',
  '#3F6C4D': 'green',
  '#1F1E1D': 'blue',
  '#a855f7': 'purple',
  '#eb2f96': 'pink',
  '#ec4899': 'pink',
}

function _mapAgentColor(hexColor: string): AgentEmployee['color'] {
  return AGENT_COLOR_MAP[hexColor?.toLowerCase()] ?? 'blue'
}

/** 从 Agent 的 category/description 提取展示技能标签 */
function _extractSkills(agent: AgentRegistryItem): SkillItem[] {
  const desc = agent.description ?? ''
  // 按中文顿号或英文逗号拆分描述，取前 3 个作为技能标签
  const parts = desc.split(/[、,，]/).map(s => s.trim()).filter(Boolean)
  if (parts.length === 0) return [{ icon: 'apps', text: agent.role ?? '智能助手' }]
  return parts.slice(0, 3).map(text => ({ icon: 'apps', text }))
}

/** 获取 Agent 状态文本 */
function _getStatusText(agent: AgentRegistryItem): string {
  if (!agent.enabled) return '即将上线'
  // 优先使用 suggestedQueries 的第一条作为能力预览
  const chatConfig = AGENT_CHAT_CONFIGS[agent.id]
  if (chatConfig?.suggestedQueries?.[0]) {
    return chatConfig.suggestedQueries[0]
  }
  return agent.description ?? '在线就绪'
}

// ============================================
// 智能体数据（从 agentRegistry 动态生成）
// ============================================

const agentEmployees = computed<AgentEmployee[]>(() =>
  agentRegistryStore.businessAgents.map(agent => ({
    key: agent.id,
    name: agent.label,
    avatarUrl: agent.avatarUrl,
    avatarText: AGENT_CHAT_CONFIGS[agent.id]?.avatarText ?? agent.shortLabel ?? agent.label[0],
    title: agent.role ?? '',
    skills: _extractSkills(agent),
    status: agent.enabled ? 'online' as const : 'offline' as const,
    statusText: _getStatusText(agent),
    color: _mapAgentColor(agent.color),
    route: agent.routePath,
  }))
)

// ============================================
// 过滤逻辑
// ============================================

const filteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return agentEmployees.value

  return agentEmployees.value.filter(agent =>
    agent.name.toLowerCase().includes(query) ||
    agent.title.toLowerCase().includes(query) ||
    agent.skills.some(skill => skill.text.toLowerCase().includes(query)) ||
    agent.statusText.toLowerCase().includes(query)
  )
})

function avatarStatus(status: AgentEmployee['status']): AgentAvatarStatus {
  if (status === 'offline') {
    return 'offline'
  }
  if (status === 'busy') {
    return 'working'
  }
  return 'online'
}

// ============================================
// 事件处理
// ============================================

function handleSearch() {
  selectedIndex.value = 0
  if (listRef.value) {
    listRef.value.scrollTop = 0
  }
}

function handleKeyDown(event: KeyboardEvent) {
  switch (event.key) {
    case 'Escape':
      event.preventDefault()
      handleClose()
      break
    case 'ArrowDown':
      event.preventDefault()
      if (selectedIndex.value < filteredAgents.value.length - 1) {
        selectedIndex.value++
        scrollToSelected()
      }
      break
    case 'ArrowUp':
      event.preventDefault()
      if (selectedIndex.value > 0) {
        selectedIndex.value--
        scrollToSelected()
      }
      break
    case 'Enter':
      event.preventDefault()
      if (filteredAgents.value.length > 0) {
        handleSelectAgent(filteredAgents.value[selectedIndex.value])
      }
      break
  }
}

function scrollToSelected() {
  nextTick(() => {
    if (listRef.value) {
      const items = listRef.value.querySelectorAll('.agent-item')
      if (items[selectedIndex.value]) {
        items[selectedIndex.value].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      }
    }
  })
}

function handleSelectAgent(agent: AgentEmployee) {
  if (agent.status === 'offline') return
  emit('select', agent)
  handleClose()

  // 始终携带 agent 参数，确保工作台能正确选中该 Agent 并显示标签
  router.push({ path: '/agents/workspace', query: { agent: agent.key } })
}

function handleClose() {
  visible.value = false
  searchQuery.value = ''
  selectedIndex.value = 0
}

// ============================================
// 生命周期
// ============================================

watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal) {
    nextTick(() => {
      searchInputRef.value?.focus()
    })
  }
})

watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
  if (newVal) {
    nextTick(() => {
      searchInputRef.value?.focus()
      searchQuery.value = ''
      selectedIndex.value = 0
    })
  }
})

onMounted(() => {
  detectOS()
})
</script>

<style scoped lang="scss">
// ============================================
// CSS 变量定义
// ============================================

:root {
  --cmd-bg: var(--surface-1);
  --cmd-border: var(--hairline);
  --cmd-text-primary: var(--text);
  --cmd-text-secondary: var(--text-muted);
  --cmd-text-muted: var(--text-faint);
  --cmd-fill-hover: var(--surface-2);
  --cmd-fill-active: var(--accent-soft);
  --cmd-accent: var(--accent);
}

// ============================================
// 遮罩层
// ============================================

.commander-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--backdrop);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 9999;
}

// ============================================
// 弹窗主体
// ============================================

.commander-modal {
  width: 640px;
  max-height: 80vh;
  background: var(--surface-1);
  border: 1px solid var(--hairline);
  border-radius: 12px;
  box-shadow:
    0 1px 0 rgba(31, 30, 29, 0.04),
    0 16px 40px -8px rgba(31, 30, 29, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: var(--c-font-sans, 'Geist', 'Inter', sans-serif);
}

// ============================================
// 顶部搜索区
// ============================================

.search-header {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 20px;
  border-bottom: 1px solid var(--hairline);
  gap: 14px;
  flex-shrink: 0;
}

.search-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--surface-3);
  flex-shrink: 0;
  color: var(--text-muted);
}

.search-input {
  flex: 1;
  height: 44px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 17px;
  font-weight: 400;
  color: var(--text);
  letter-spacing: -0.01em;

  &::placeholder {
    color: var(--text-faint);
    font-weight: 400;
  }
}

.esc-badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 6px;
  background: var(--surface-3);
  border: 1px solid var(--hairline);
  font-family: 'SF Mono', Monaco, 'Cascadia Code', Consolas, monospace;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-muted);
  letter-spacing: 0.02em;
}

// ============================================
// 中间列表区
// ============================================

.list-container {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  min-height: 200px;
  max-height: calc(80vh - 64px - 48px);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--hairline);
    border-radius: 3px;

    &:hover {
      background: color-mix(in oklch, var(--hairline) 80%, var(--text));
    }
  }
}

// ============================================
// 列表项
// ============================================

.agent-item {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
  gap: 14px;
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: var(--surface-2);

    .action-button {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &.active {
    background: var(--accent-soft);

    .indicator {
      opacity: 1;
      transform: translateY(-50%) scaleY(1);
    }

    .name {
      color: var(--accent);
    }

    .action-button {
      opacity: 1;
      transform: translateX(0);
    }

    .skill-tag {
      border-color: var(--accent);
      background: var(--accent-soft);
      color: var(--accent);

      .skill-icon {
        color: var(--accent);
      }
    }
  }
  
  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    
    &:hover {
      background: transparent;
    }
    
    .action-button {
      opacity: 0.4;
      pointer-events: none;
    }
  }
}

// 左侧蓝色指示条
.indicator {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 4px;
  height: 48px;
  background: var(--accent);
  border-radius: 0 4px 4px 0;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;

  &.visible {
    opacity: 1;
    transform: translateY(-50%) scaleY(1);
  }
}

// ============================================
// 头像区域
// ============================================

.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
}

.status-dot {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2.5px solid var(--surface-1);
  box-shadow: none;
  z-index: 2;
}

// ============================================
// 信息区域
// ============================================

.info-section {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 2px;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: 0.01em;
  transition: color 0.15s ease;
}

.title-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.skills-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.skill-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 6px;
  background: var(--surface-1);
  border: 1px solid var(--hairline);
  font-size: 11px;
  color: var(--text-muted);
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.skill-icon {
  display: flex;
  align-items: center;
  color: var(--text-faint);
  transition: color 0.15s ease;
}

.skill-text {
  line-height: 1;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.status-icon {
  font-size: 12px;
  line-height: 1;
}

.status-text {
  font-size: 12px;
  color: var(--text-faint);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.4;
}

// ============================================
// 右侧操作按钮
// ============================================

// Claude 素色：呼叫按钮 墨黑底白字（claude.ai 主按钮规则）
.action-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--accent);
  color: var(--accent-ink);
  font-size: 12px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  align-self: center;
  font-family: var(--c-font-sans);

  &.visible {
    opacity: 1;
  }

  &:hover {
    background: var(--accent-hover);
  }
}

.enter-key {
  font-family: var(--c-font-mono, 'Geist Mono', monospace);
  font-size: 14px;
  font-weight: 500;
}

.enter-text {
  letter-spacing: 0.02em;
}

// ============================================
// 空状态
// ============================================

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-muted);
  margin-bottom: 6px;
}

.empty-hint {
  font-size: 13px;
  color: var(--text-faint);
}

// ============================================
// 底部栏
// ============================================

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 20px;
  background: var(--surface-2);
  border-top: 1px solid var(--hairline);
  flex-shrink: 0;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.footer-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
}

.footer-count {
  font-size: 12px;
  color: var(--text-faint);
  padding: 2px 8px;
  background: var(--surface-3);
  border-radius: 4px;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.shortcut-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.shortcut-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 5px;
  background: var(--surface-1);
  border: 1px solid var(--hairline);
  font-family: 'SF Mono', Monaco, 'Cascadia Code', Consolas, monospace;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-muted);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.shortcut-label {
  font-size: 12px;
  color: var(--text-faint);
}

.shortcut-divider {
  width: 1px;
  height: 16px;
  background: var(--hairline);
}

// ============================================
// 动画
// ============================================

.commander-fade-enter-active,
.commander-fade-leave-active {
  transition: opacity 0.15s ease;
  
  .commander-modal {
    transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.15s ease;
  }
}

.commander-fade-enter-from,
.commander-fade-leave-to {
  opacity: 0;
  
  .commander-modal {
    transform: translateY(-20px) scale(0.96);
    opacity: 0;
  }
}

// ============================================
// 暗黑模式
// ============================================

:global(body[arco-theme='dark']) {
  .commander-modal {
    background: var(--surface-1);
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.05),
      0 25px 50px -12px rgba(0, 0, 0, 0.5);
  }

  .search-header {
    border-bottom-color: var(--hairline);
  }

  .search-icon-wrapper {
    background: var(--surface-3);
    color: var(--text-muted);
  }

  .search-input {
    color: var(--text);

    &::placeholder {
      color: var(--text-faint);
    }
  }

  .esc-badge {
    background: var(--surface-3);
    border-color: var(--hairline);
    color: var(--text-muted);
  }

  .avatar {
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  .status-dot {
    border-color: var(--surface-1);
  }

  .agent-item {
    &:hover {
      background: var(--surface-2);
    }

    &.active {
      background: var(--accent-soft);

      .name {
        color: var(--accent);
      }

      .skill-tag {
        background: color-mix(in oklch, var(--accent) 20%, transparent);
        border-color: color-mix(in oklch, var(--accent) 40%, transparent);
        color: var(--accent);

        .skill-icon {
          color: var(--accent);
        }
      }
    }
  }

  .indicator {
    background: var(--accent);
  }

  .name {
    color: var(--text);
  }

  .skill-tag {
    background: var(--surface-2);
    border-color: var(--hairline);
    color: var(--text-muted);

    .skill-icon {
      color: var(--text-faint);
    }
  }

  .status-text {
    color: var(--text-faint);
  }

  .footer {
    background: var(--surface-2);
    border-top-color: var(--hairline);
  }

  .footer-label {
    color: var(--text-muted);
  }

  .footer-count {
    background: var(--surface-3);
    color: var(--text-faint);
  }

  .shortcut-key {
    background: var(--surface-3);
    border-color: var(--hairline);
    color: var(--text-muted);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  .shortcut-label {
    color: var(--text-faint);
  }

  .shortcut-divider {
    background: var(--hairline);
  }

  .empty-text {
    color: var(--text-muted);
  }

  .empty-hint {
    color: var(--text-faint);
  }
}

// ============================================
// 响应式
// ============================================

@media (max-width: 680px) {
  .commander-modal {
    width: calc(100vw - 32px);
    max-height: calc(100vh - 52px);
  }

  .search-header {
    height: 56px;
    padding: 0 16px;
  }

  .search-input {
    font-size: 16px;
  }

  .agent-item {
    padding: 12px 14px;
    gap: 12px;
  }

  .avatar-wrapper,
  .avatar {
    width: 44px;
    height: 44px;
  }

  .avatar-text {
    font-size: 18px;
  }

  .skills-row {
    gap: 4px;
  }

  .skill-tag {
    padding: 2px 6px;
    font-size: 10px;
  }

  .footer {
    height: 44px;
    padding: 0 16px;
  }

  .footer-right {
    gap: 8px;
  }

  .shortcut-group:nth-child(n+3) {
    display: none;
  }
}
</style>
